import { ReactNode, useCallback, useRef, useState } from 'react'
import _ from 'lodash'
import { faReply, faRotateLeft } from '@fortawesome/pro-regular-svg-icons'
import { faSignature, faTrashCan } from '@fortawesome/pro-solid-svg-icons'
import styled from '@emotion/styled'
import { FusionColor } from '../../../theme/FusionStyle'
import { ConceptStatus } from '@fusion-advantage/backcompat'
import Loading from '../../Loading'
import L1SignaturePad from '../l1/L1SignaturePad'
import { L1ReasonableButton } from '../l1/L1Button'
import { L2Dialog, L2DialogActions, L2DialogContent } from '../l2/L2Dialog'
import { L3Image, L3ImageFormat } from './L3Image'
import L2Surface from '../l2/L2Surface'
import L2ControlGrid from '../l2/L2ControlGrid'
import L2ControlGridLayout from '../l2/L2ControlGridLayout'
import { L4NoPrint } from '../l4/L4Print'
import SignaturePad from 'react-signature-pad-wrapper'

const FormSignatureRow = styled.div(({theme}) => ({
	display: 'grid',
	gridTemplateColumns: '1fr max-content max-content',
	alignItems: 'center',
	gap: theme.spacing(2),
}))

const FormSignatureImageContainer = styled.div(({theme}) => ({
	height: theme.spacing(7),
}))

const DialogContent = styled(L2DialogContent)({
	flexDirection: 'column',
})

interface L3SignatureProps {
	conceptStatus: ConceptStatus|null,
	onSave: (svg: string) => void
	onDelete: () => void
	loading?: Boolean
}

export const L3Signature = ({ conceptStatus, onSave, onDelete, loading }: L3SignatureProps) => {
	const padRef = useRef<SignaturePad>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [isDirty, setIsDirty] = useState(false)
	const hasPersisted = Boolean(conceptStatus?.value)
	const isEditing = isOpen && !hasPersisted
	let signatureDate: string|null = null
	if(_.isString(conceptStatus?.updatedAt)) {
		signatureDate = conceptStatus.updatedAt.substring(0, 10)
	}
	const handleDraw = useCallback(() => {
		setIsDirty(true)
	}, [])
	const handleReset = useCallback(() => {
		padRef.current?.clear()
		setIsDirty(false) 
	}, [])
	const handleSave = useCallback(async () => {
		const svg = padRef.current?.toSVG()
		if(svg) {
			onSave(svg)
			setIsOpen(false)
		} else {
			alert('Expected svg; signature may not have saved.  Please contact support.')
		}
	}, [onDelete, onSave])
	const handleDelete = useCallback(() => {
		onDelete()
		handleReset()
	}, [onDelete, handleReset])
	const handleClose = useCallback(() => {
		setIsDirty(false)
		setIsOpen(false)
	}, [])
	let dialogBody: ReactNode
	if(isEditing) {
		let buttons: ReactNode = null
		if(loading) {
			buttons = <Loading />
		} else if(isDirty) {
			buttons = <>
				<L1ReasonableButton
					label="Reset"
					color={FusionColor.White}
					icon={faRotateLeft}
					onClick={handleReset}
				/>
				<L1ReasonableButton
					label="Save"
					onClick={handleSave}
				/>
			</>
		} else {
			buttons = <L1ReasonableButton
				label="Close"
				color={FusionColor.White}
				icon={faReply}
				onClick={handleClose}
			/>
		}
		dialogBody = <>
			<DialogContent>
				<L1SignaturePad
					ref={padRef}
					onBeginStroke={handleDraw}
				/>
			</DialogContent>
			<L2DialogActions>
				<L2ControlGrid layout={L2ControlGridLayout.FlexRow} columnCount={2}>
					{buttons}
				</L2ControlGrid>
			</L2DialogActions>
		</>
	} else {
		dialogBody = <>
			<DialogContent>
				<L3Image
					format={L3ImageFormat.SvgString}
					conceptStatus={conceptStatus}
				/>
				{signatureDate ? <div>{signatureDate}</div> : null}
			</DialogContent>
			<L2DialogActions>
				<L1ReasonableButton
					label="Delete"
					color={FusionColor.StopRed}
					icon={faTrashCan}
					onClick={handleDelete}
				/>
			</L2DialogActions>
		</>
	}

	return (
		<>
			<L2Dialog
				show={isOpen}
				onClose={handleClose}
				title="Signature"
			>
				{dialogBody}
			</L2Dialog>
			<L2Surface>
				<FormSignatureRow>
					{hasPersisted ? (
						<FormSignatureImageContainer>
							<L3Image
								format={L3ImageFormat.SvgString}
								conceptStatus={conceptStatus}
							/>
						</FormSignatureImageContainer>
					) : <div />}
					<div>{signatureDate}</div>
					<L4NoPrint>
						<div>
							<L1ReasonableButton
								label={hasPersisted ? 'View' : 'Sign'}
								icon={faSignature}
								onClick={() => setIsOpen(true)}
							/>
						</div>
					</L4NoPrint>
				</FormSignatureRow>
			</L2Surface>
		</>
	)
}

export default L3Signature
